import { type RefObject, useEffect, useState } from 'react';

import { measureVisibleBBox } from './measure-visible-bbox';
import { raceFrame } from '../helpers/race-frame';
import { boxToExtent, type ContentExtent } from './compute-fitted-viewbox';

/** Frames to keep measuring before an unchanged result is accepted as final. */
const MIN_FRAMES = 20;
const STABLE_FRAMES = 6;
const MAX_FRAMES = 300;

const round = (n: number): number => Math.round(n * 100) / 100;

export type UseStabilizedInkExtentParams = {
	groupRef: RefObject<SVGGElement | null>;
	/** When false the loop never runs and null is returned (pages that fit differently). */
	enabled?: boolean;
	/** Change this to restart measurement -- e.g. controller identity or font factor. */
	resetKey?: string;
};

/**
 * Measures the rendered ink extent of a content group and keeps re-measuring
 * until it holds steady, because a partial first render can look settled for a
 * few frames before the dimension labels finish laying out. Late web-font loading
 * reshapes label ink, so the loop restarts when fonts land.
 * @param params the content group ref, whether to measure, and a restart key
 */
export const useStabilizedInkExtent = (params: UseStabilizedInkExtentParams): ContentExtent | null => {
	const { groupRef, enabled = true, resetKey = '' } = params;
	const [extent, setExtent] = useState<ContentExtent | null>(null);

	useEffect(() => {
		if (!enabled) return;
		const group = groupRef.current;
		if (!group) return;

		let cancelFrame: (() => void) | null = null;
		let disposed = false;
		let frames = 0;
		let stable = 0;
		let prevKey = '';

		const tick = (): void => {
			frames += 1;

			let box: ReturnType<typeof measureVisibleBBox> = null;
			try {
				box = measureVisibleBBox(group);
			} catch (_error) {
				box = null;
			}

			if (box && box.width > 0 && box.height > 0) {
				const next = boxToExtent({
					x: round(box.x),
					y: round(box.y),
					width: round(box.width),
					height: round(box.height)
				});
				const key = `${next.left}|${next.right}|${next.top}|${next.bottom}`;
				if (key === prevKey) {
					stable += 1;
				} else {
					stable = 0;
					prevKey = key;
					setExtent(next);
				}
			}

			if ((frames < MIN_FRAMES || stable < STABLE_FRAMES) && frames < MAX_FRAMES) {
				cancelFrame = raceFrame(tick);
			}
		};

		// restart on both settle and failure — either way the ink is worth re-reading
		const restart = (): void => {
			if (disposed) return;
			frames = 0;
			stable = 0;
			cancelFrame?.();
			cancelFrame = raceFrame(tick);
		};

		document.fonts?.ready?.then(restart, restart);

		cancelFrame = raceFrame(tick);

		return () => {
			disposed = true;
			cancelFrame?.();
		};
	}, [enabled, groupRef, resetKey]);

	return extent;
};
